/**
 * Scan-to-sign-in, the browser half.
 *
 * Three steps, matching devices/link_views.py:
 *
 *   1. open a handshake  -> { link_id, nonce, expires_at, poll_interval_ms }
 *   2. render link_id + device_id as a QR; the phone scans and approves
 *   3. wait on ws/v1/link/<id>/?nonce=... until the server pushes the token
 *
 * The nonce never goes in the QR. It stays in memory here and is the only
 * thing that can collect the token, so somebody photographing the screen sees
 * which handshake is in flight and can do nothing with it.
 */
import { api, ApiError } from './api';
import { localIdentity } from '../crypto/keys';
import { describeThisBrowser, getDeviceId } from './device';
import { setSession, type Session } from './session';

export type LinkHandshake = {
  linkId: string;
  /** Secret. Never render this, never put it in the QR. */
  nonce: string;
  expiresAt: number;
  pollIntervalMs: number;
  /** Relative path of the socket to wait on. */
  socketPath: string;
  /** Exactly what to encode in the QR image. */
  qrPayload: string;
};

type RequestResponse = {
  link_id: string;
  nonce: string;
  expires_at: string;
  poll_interval_ms: number;
  socket_path: string;
};

type PollResponse =
  | { status: 'pending' }
  | {
      status: 'approved';
      token: string;
      device_id: string;
      user: Session['user'];
      tenant: { slug: string };
    };

/**
 * The QR payload. Short keys because QR density grows with length and a
 * denser code is harder for a phone camera to read at arm's length.
 *
 *   v - payload version, so the scanner can refuse a shape it predates
 *   l - link id
 *   d - device id
 *   c - curve25519 identity key
 *   e - ed25519 fingerprint key
 *
 * The keys are here so the phone can Olm-encrypt room keys straight to this
 * browser the instant it approves, without waiting for the browser to sign in
 * and publish to the directory. That ordering matters: approval happens
 * before this browser has a token.
 *
 * Still v1. The two new fields are additive and an older scanner ignores
 * them, so bumping the version would refuse codes that would have worked.
 */
function qrPayload(
  linkId: string,
  deviceId: string,
  curve25519: string,
  ed25519: string,
): string {
  return JSON.stringify({ v: 1, l: linkId, d: deviceId, c: curve25519, e: ed25519 });
}

export async function openHandshake(signal?: AbortSignal): Promise<LinkHandshake> {
  const deviceId = getDeviceId();
  // Mint the Olm account now if it does not exist, so its public keys can
  // ride in the QR. Local only - publishing needs a token we do not have yet.
  const identity = await localIdentity();
  const res = await api<RequestResponse>('/v1/devices/link/request/', {
    method: 'POST',
    anonymous: true,
    signal,
    body: { device_id: deviceId, platform: 'web', label: describeThisBrowser() },
  });
  return {
    linkId: res.link_id,
    nonce: res.nonce,
    expiresAt: Date.parse(res.expires_at),
    pollIntervalMs: res.poll_interval_ms,
    socketPath: res.socket_path,
    qrPayload: qrPayload(res.link_id, deviceId, identity.curve25519, identity.ed25519),
  };
}

export class HandshakeExpiredError extends Error {
  constructor() {
    super('This code has expired.');
    this.name = 'HandshakeExpiredError';
  }
}

/** The socket could not be established or dropped without a verdict. */
class SocketUnavailableError extends Error {
  constructor(readonly code?: number) {
    super('Link socket unavailable.');
    this.name = 'SocketUnavailableError';
  }
}

/**
 * Wait for the phone to approve.
 *
 * The socket is the normal path: the server pushes the token the instant the
 * phone approves, and an idle sign-in screen costs one connection instead of
 * a request every 1.5 seconds for two minutes.
 *
 * Polling survives only as a fallback, for a network that will not upgrade a
 * WebSocket - a corporate proxy, a captive portal. Both transports go through
 * the same `collect()` on the server, so the single-use guarantee holds even
 * if this browser somehow tried both.
 */
export async function waitForApproval(
  handshake: LinkHandshake,
  signal: AbortSignal,
): Promise<Session> {
  try {
    return await waitOnSocket(handshake, signal);
  } catch (err) {
    if (err instanceof SocketUnavailableError) {
      // Not an error the person should see: the flow still works, it just
      // costs more requests. Worth a console line so a broken WS deploy is
      // visible rather than silently degrading everyone to polling.
      console.warn(
        `[nsibi] link socket unavailable (code ${err.code ?? "none"}), falling back to polling`,
      );
      return pollForApproval(handshake, signal);
    }
    throw err;
  }
}

function socketUrl(handshake: LinkHandshake): string {
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${location.host}${handshake.socketPath}?nonce=${encodeURIComponent(handshake.nonce)}`;
}

function waitOnSocket(handshake: LinkHandshake, signal: AbortSignal): Promise<Session> {
  return new Promise<Session>((resolve, reject) => {
    let settled = false;
    const ws = new WebSocket(socketUrl(handshake));

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener('abort', onAbort);
      document.removeEventListener('visibilitychange', onVisible);
      fn();
    };

    // A phone browser that opened the APP to approve ("Open Nsibi") is frozen
    // in the background while the approval lands, and Chrome can drop the
    // socket then without telling the page - no message, no close. The page
    // would wait on a dead socket for ever. So whenever it is shown again,
    // ask once over HTTP: the server lets this browser (it holds the nonce)
    // collect again shortly after the socket's attempt (devices/linking.py).
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || settled) return;
      void collectOnce(handshake, signal)
        .then((outcome) => {
          if (outcome === 'pending') return;
          try {
            ws.close();
          } catch {
            /* already closing */
          }
          finish(() => resolve(outcome));
        })
        .catch((err) => {
          if (err instanceof HandshakeExpiredError) finish(() => reject(err));
          // Anything else (offline for a moment): the socket may still answer.
        });
    };
    document.addEventListener('visibilitychange', onVisible);

    const onAbort = () => {
      try {
        ws.close();
      } catch {
        /* already closing */
      }
      finish(() => reject(new DOMException('Aborted', 'AbortError')));
    };
    signal.addEventListener('abort', onAbort, { once: true });

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as {
          status: string;
          token: string;
          user: Session['user'];
          tenant: { slug: string };
        };
        if (data.status !== 'approved') return;
        const session: Session = {
          token: data.token,
          tenantSlug: data.tenant.slug,
          user: data.user,
        };
        setSession(session);
        finish(() => resolve(session));
      } catch {
        finish(() => reject(new SocketUnavailableError()));
      }
    };

    ws.onclose = (event) => {
      // 4404 unknown/expired/wrong-nonce, 4409 already collected. Both are
      // verdicts, not transport failures, so they must not trigger a poll
      // that would only get the same answer.
      if (event.code === 4404 || event.code === 4409) {
        finish(() => reject(new HandshakeExpiredError()));
        return;
      }
      finish(() => reject(new SocketUnavailableError(event.code)));
    };

    ws.onerror = () => {
      // onclose always follows, and carries the code; let it decide.
    };
  });
}

/** Matches the server's APPROVED_COLLECT_SECONDS (devices/linking.py). */
const APPROVED_GRACE_MS = 120_000;

/**
 * One HTTP collection: the session, 'pending', or HandshakeExpiredError.
 * The fallback poll is this in a loop; a page coming back into view asks it
 * once.
 */
async function collectOnce(handshake: LinkHandshake, signal: AbortSignal): Promise<Session | 'pending'> {
  let res: PollResponse;
  try {
    res = await api<PollResponse>(
      `/v1/devices/link/${handshake.linkId}/?nonce=${encodeURIComponent(handshake.nonce)}`,
      { anonymous: true, signal },
    );
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 409)) {
      throw new HandshakeExpiredError();
    }
    throw err;
  }
  if (res.status !== 'approved') return 'pending';
  const session: Session = {
    token: res.token,
    tenantSlug: res.tenant.slug,
    user: res.user,
  };
  setSession(session);
  return session;
}

/** Fallback only. See waitForApproval. */
async function pollForApproval(
  handshake: LinkHandshake,
  signal: AbortSignal,
): Promise<Session> {
  for (;;) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    // The server decides when a code is gone: an APPROVED code stays
    // collectable after its own two minutes (a phone that approved in the
    // app and came back late). This bound only stops a poll that would
    // otherwise never end.
    if (Date.now() >= handshake.expiresAt + APPROVED_GRACE_MS) throw new HandshakeExpiredError();
    const outcome = await collectOnce(handshake, signal);
    if (outcome !== 'pending') return outcome;
    await sleep(handshake.pollIntervalMs, signal);
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
